import Axios, { AxiosResponse } from 'axios';

import { PortalConfig as PortalConfigContextType } from 'src/context';
import { PortalConfig as PortalConfigEntityType } from 'src/entities/PortalConfig';
import { PortalSetupInput } from 'src/routes/onboarding/setup/types';
import { getCookie } from 'src/utils/StorageUtils';

const BRAND_API_URL = 'https://ai-preview-dusky.vercel.app/api/customize';

export interface BrandData {
  companyName: string;
  colors: {
    sidebarBackground: string;
    sidebarText: string;
    accent: string;
  };
  images: {
    squareIcon?: string | null;
    fullLogo?: string | null;
    loginImage?: string | null;
    loginGradientImage?: string | null;
    dashboardImage?: string | null;
  };
}

function isBrandData(data: unknown): data is BrandData {
  if (typeof data !== 'object' || data === null) return false;
  return (
    'companyName' in data &&
    typeof (data as BrandData).companyName === 'string' &&
    'colors' in data &&
    typeof (data as BrandData).colors === 'object' &&
    'images' in data &&
    typeof (data as BrandData).images === 'object'
  );
}

export default class OnboardingClient {
  /**
   * Prefetch brand customization data based on the user's email domain.
   * Called early in onboarding (after email step) so the ~11s API call
   * overlaps with the user filling subsequent steps.
   * Calls Ana's brand API directly (CORS-enabled with Access-Control-Allow-Origin: *).
   */
  static async FetchBrandCustomization(
    email: string,
  ): Promise<BrandData | null> {
    try {
      const res: { data: { success?: boolean; data?: unknown } } = await Axios({
        method: 'post',
        url: BRAND_API_URL,
        data: { input: email },
      });
      // Ana's API wraps data in { success: true, data: { ... } }
      if (res.data?.success && isBrandData(res.data.data)) {
        return res.data.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  static SavePortalOnboarding(
    portalConfig: PortalConfigContextType,
    values: PortalSetupInput,
  ): Promise<AxiosResponse<PortalConfigEntityType>> {
    const portalApiBaseURL = portalConfig.AWS.API.endpoints[2].endpoint;
    const fbBrowserId = getCookie('_fbp'); // get Facebook browser ID value from _fbp cookie
    const fbClickId = getCookie('_fbc'); // get Facebook click ID value from the _fbc cookie
    const googleClickId = getCookie('_gcl_aw'); // get Google click ID value from the _gcl_aw cookie
    const tiktokBrowserId = getCookie('_ttp'); // get TikTok browser/pixel ID value from the _ttp cookie
    const tiktokClickId = getCookie('ttclid'); // get TikTok click ID value from the ttclid cookie
    const linkedinClickId = getCookie('li_fat_id'); // get LinkedIn click ID value from the li_fat_id cookie
    const { brandData, ...rest } = values;
    return Axios({
      method: 'post',
      url: `${portalApiBaseURL}/portal/any/setup`,
      data: {
        ...rest,
        fbBrowserId,
        fbClickId,
        googleClickId,
        tiktokBrowserId,
        tiktokClickId,
        linkedinClickId,
        ...(brandData ? { brandData } : {}),
      },
    });
  }

  static GetPortalSetup(
    portalConfig: PortalConfigContextType,
    stackId: string,
    email: string,
    password: string,
  ): Promise<AxiosResponse<string>> {
    const portalApiBaseURL = portalConfig.AWS.API.endpoints[2].endpoint;
    return Axios({
      method: 'get',
      url: `${portalApiBaseURL}/portal/any/setup`,
      params: {
        stackId,
        email,
        password,
      },
    });
  }

  static GetPortalURLHealth(portalURL: string): Promise<AxiosResponse<string>> {
    return Axios({
      method: 'get',
      url: `${portalURL}/health`,
    });
  }
}
